import { X } from "@/components/logos/X";
import {
  UserCircle,
  MapPin,
  Calendar,
  Users,
  AlertCircle,
  ArrowRightCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/api";
import TwitterMediaGrid from "@/components/twitter/TwitterMedia";
import {
  formatDate,
  formatNumber,
  processReplyData,
} from "@/components/twitter/twitterUtils";

const SkippedReplyCard = ({ reply, personaId }: any) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Process the data using our shared utility
  const processedData = useMemo(() => {
    // Get the base data from our shared utility
    const baseData = processReplyData(reply);

    // Add skipped-specific fields
    const metadata = reply?.metadata || {};
    const reason = metadata.reason || reply?.reason || "No reason provided";
    const ruleViolated =
      metadata.rule_violated || reply?.rule_violated || "unknown";

    // Ensure values are strings, not objects
    return {
      ...baseData,
      reason:
        typeof reason === "object" ? JSON.stringify(reason) : String(reason),
      ruleViolated:
        typeof ruleViolated === "object"
          ? JSON.stringify(ruleViolated)
          : String(ruleViolated),
      createdAt: reply?.created_at || "",
    };
  }, [reply]);

  // Open tweet in new tab
  const openTweetInNewTab = () => {
    if (processedData.tweetId && processedData.username) {
      window.open(
        `https://twitter.com/${processedData.username}/status/${processedData.tweetId}`,
        "_blank",
      );
    }
  };

  // Process the skipped reply
  const processSkippedReply = async () => {
    try {
      // Call API to process the skipped reply
      await apiClient.post("/api/twitter-replies/process-skipped", {
        persona_id: personaId,
        doc_id: reply.doc_id,
      });

      // Show success message
      toast({
        description: "Reply has been moved to pending queue",
      });

      // Refresh the skipped replies list
      queryClient.invalidateQueries({
        queryKey: ["skipped-replies", personaId],
      });
    } catch (error: any) {
      console.error("Error processing skipped reply:", error);
      toast({
        description: `Failed to process: ${error.message || "Unknown error"}`,
        variant: "destructive",
      });
    }
  };

  // Safe rendering helper function
  const safeRender = (value: any) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  return (
    <div className="bg-background border rounded-2xl">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 border-b border-border">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-amber-500/10 text-amber-500">
              Skipped Reply
            </Badge>
            <span className="text-sm text-muted-foreground">
              {safeRender(formatDate(processedData.createdAt))}
            </span>
          </div>
          <X width={16} height={16} fill="white" />
        </div>

        {/* Original message */}
        <div className="border rounded-xl p-3 bg-gray-100 text-black">
          <div className="flex items-start gap-2">
            {/* User Avatar */}
            <div className="flex-shrink-0">
              {processedData.profileImage ? (
                <a
                  href={`https://twitter.com/${processedData.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-90 transition-opacity"
                >
                  <img
                    src={processedData.profileImage}
                    alt={`${safeRender(processedData.displayName)}'s avatar`}
                    className="w-10 h-10 rounded-full"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.style.display = "none";

                      // Create fallback icon container
                      const div = document.createElement("div");
                      div.className =
                        "w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center";

                      // Create icon inside container
                      const icon = document.createElement("div");
                      icon.innerHTML =
                        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-gray-500"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>';
                      div.appendChild(icon);

                      // Replace img with div
                      e.currentTarget.parentNode?.replaceChild(
                        div,
                        e.currentTarget,
                      );
                    }}
                  />
                </a>
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <UserCircle className="w-5 h-5 text-gray-500" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {/* User Info Header */}
              <div className="flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col">
                    <div className="font-bold text-black line-clamp-1">
                      {safeRender(processedData.displayName)}
                      {processedData.verified && (
                        <span className="ml-1 inline-flex items-center justify-center bg-blue-500 text-white rounded-full w-4 h-4 text-sm">
                          ✓
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-sm">
                      @{safeRender(processedData.username)}
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 flex-shrink-0">
                    {safeRender(formatDate(processedData.timestamp))}
                  </span>
                </div>

                {/* User meta information */}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 mt-1 mb-2">
                  {processedData.location && (
                    <div className="flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      <span>{safeRender(processedData.location)}</span>
                    </div>
                  )}
                  {processedData.joinedDate && (
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        Joined{" "}
                        {safeRender(
                          formatDate(processedData.joinedDate, {
                            month: "long",
                            year: "numeric",
                            day: undefined,
                            hour: undefined,
                            minute: undefined,
                          }),
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    {processedData.followersCount != null ? (
                      <span>
                        {formatNumber(processedData.followersCount)} followers
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Tweet content */}
              <p className="mt-1 text-black text-sm">
                {safeRender(processedData.tweetText)}
              </p>
            </div>
          </div>

          {/* Using our shared TwitterMediaGrid component */}
          {Array.isArray(processedData.mediaUrls) && (
            <TwitterMediaGrid urls={processedData.mediaUrls} />
          )}

          {/* Thread context */}
          {Array.isArray(processedData.threadContext) &&
            processedData.threadContext.length > 1 && (
              <div className="mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openTweetInNewTab}
                  className="text-gray-600 hover:text-white hover:bg-gray-800"
                >
                  Show full conversation in new tab
                </Button>
              </div>
            )}
        </div>
      </div>

      {/* Skipped Reason Section */}
      <div className="px-6 py-4">
        <div className="flex items-start gap-2 mb-3">
          <AlertCircle className="h-5 w-5 text-amber-500 mt-1" />
          <div className="flex-1">
            <div className="font-medium">Reason for skipping:</div>
            <p className="text-sm text-muted-foreground">
              {safeRender(processedData.reason)}
            </p>

            {processedData.ruleViolated &&
              !["none", "unknown"].includes(processedData.ruleViolated) && (
                <div className="mt-2">
                  <div className="font-medium">Rule violated:</div>
                  <Badge variant="outline" className="mt-1">
                    {safeRender(processedData.ruleViolated)}
                  </Badge>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 py-3 border-t flex justify-end">
        <Button variant="default" onClick={processSkippedReply}>
          <ArrowRightCircle className="w-4 h-4 mr-2" />
          Process Reply
        </Button>
      </div>
    </div>
  );
};

export default SkippedReplyCard;
